import React, { useState } from 'react';
import { isPlainObject } from 'lodash';

import { addLocationOriginToUrl } from '@zapier/url-utils';

import { createId } from 'app/common/CommonUtils';
import { queryToDataSet } from 'app/developer-v3/components/PartnerSupportForm/utils/tree';
import {
  useQueryIntegrationsImplementations,
  useQueryZapierSession,
  IntegrationsImplementations,
} from 'app/developer-v3/hooks';
import { useEligibility } from 'app/developer-v3/teams/team-technical-support-operations/get-help';

type ZapType = {
  id: string | null;
  title: string | null;
  nodes: {
    id: string;
    selectedApi: string | null;
    appImages: {
      url_64x64: string | null;
    } | null;
  }[];
};

type DataSet = {
  [key: string]: string | number | number[]; // TODO: Drop array
};

export type SizeType = 'small' | 'medium' | 'large';

type Context = {
  apps: IntegrationsImplementations;
  attemptId: string;
  doc?: string;
  from_url?: string;
  initialDataSet: DataSet;
  isEmailSupportEligible: boolean;
  isLoading: boolean;
  isPremierSupportEligible: boolean;
  lastTrackedPath: string;
  setLastTrackedPath: (path: string) => void;
  setSelectedApp: (app: any) => void; //TODO: fix
  setZap: (zap?: ZapType) => void;
  selectedApp: string;
  size: SizeType;
  user?: {
    email: string;
    fullName: string;
  };
  zap?: ZapType;
};

type Query = {
  [key: string]: string;
};

type FormProviderProps = {
  children: React.ReactChild[] | ((value: Context) => React.ReactNode);
  query: Query;
  size: SizeType;
};

const HISTORY_RE = /\/app\/history\/[a-z0-9-]+/;
const EDITOR_RE = /\/app\/editor\/([0-9]+)/;

const parseQuery = (
  query: Query
): {
  doc?: string;
  from_url?: string;
  initialDataSet: DataSet;
} => {
  let doc: string, from_url: string;

  const queryToParse = { ...query };

  if (queryToParse.doc) {
    doc = addLocationOriginToUrl(queryToParse.doc);
    delete queryToParse.doc;
  }

  if (queryToParse.from_url) {
    from_url = addLocationOriginToUrl(queryToParse.from_url);
    delete queryToParse.from_url;
  }

  if (queryToParse.error) {
    queryToParse.topic = 'troubleshooting';
    queryToParse.problem = 'error';
  }

  const initialDataSet = queryToDataSet(queryToParse);

  // derive fields from `from_url`
  if (from_url) {
    if (
      (!isPlainObject(initialDataSet.help) || !initialDataSet.help.task) &&
      HISTORY_RE.test(from_url)
    ) {
      initialDataSet.help = initialDataSet.help || {};
      initialDataSet.help.task = from_url;
    } else if (!initialDataSet.zap) {
      const match = from_url.match(EDITOR_RE);
      if (match) {
        initialDataSet.zap = match[1];
      }
    }
  }

  return {
    doc,
    from_url,
    initialDataSet,
  };
};

const ContextMissing = () => {
  throw new Error('FormContext not provided!');
};

const FormContext = React.createContext<Context>({
  apps: [],
  attemptId: createId(),
  doc: undefined,
  from_url: undefined,
  initialDataSet: {},
  isEmailSupportEligible: undefined,
  isLoading: true,
  isPremierSupportEligible: undefined,
  lastTrackedPath: undefined,
  setLastTrackedPath: ContextMissing,
  setSelectedApp: ContextMissing,
  setZap: ContextMissing,
  selectedApp: undefined,
  size: undefined,
  user: undefined,
  zap: undefined,
});
FormContext.displayName = 'FormContext';

const FormProvider = ({ children, query, size }: FormProviderProps) => {
  const [lastTrackedPath, setLastTrackedPath] = useState<string>();
  const [zap, setZap] = useState<ZapType>();
  const [selectedApp, setSelectedApp] = useState();

  const { isLoading: isLoadingUser, data: user } = useQueryZapierSession();
  const { isLoading: isLoadingEligbility, eligibility } = useEligibility();

  const attemptId = createId();

  const { doc, from_url, initialDataSet } = parseQuery(query);

  const {
    integrationsImplementations,
    isLoading: isLoadingIntegrationsImplementations,
  } = useQueryIntegrationsImplementations();

  const value = {
    attemptId,
    apps: integrationsImplementations,
    doc,
    from_url,
    initialDataSet,
    // On errors, default to true
    isEmailSupportEligible: eligibility.isEmailSupportEligible !== false,
    isLoading:
      isLoadingUser ||
      isLoadingEligbility ||
      isLoadingIntegrationsImplementations,
    isPremierSupportEligible: eligibility.isPremierSupportEligible === true,
    lastTrackedPath,
    setLastTrackedPath,
    selectedApp,
    setSelectedApp,
    setZap,
    size,
    user,
    zap,
  };

  return (
    <FormContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </FormContext.Provider>
  );
};

export const useFormContext = () => React.useContext(FormContext);

export default FormProvider;
export { FormProvider };
